import React, { useMemo } from "react";
import { useAppSelector } from "../../store";
import { localErrorsMessages } from "../../constants/errors";
import { passwordFormModes } from "./modes";
import { modesData, newPasswordValidationConditions } from "./consts";
import { InputFormElement } from "../../components/UIComponents/InputFormElement";
import { FormLayout } from "../../components/FormLayout/FormLayout";
import { ValidationSchema } from "../../utils/useValidation";
import { AuthorizationCodeInput } from "../../components/AuthorizationCodeInput/AuthorizationCodeInput";

export const PasswordForm: React.FC<{ mode: passwordFormModes }> = (props) => {
  const { errorTrKey, formData } = useAppSelector((state) => state.user);
  const initialState = {
    authorizationCode: "",
    newPassword: "",
    confirmedNewPassword: "",
  };
  const selectedMode = modesData[props.mode];
  const validationSchema: ValidationSchema<typeof initialState> = useMemo(
    () => ({
      authorizationCode: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey,
          validate: (value) =>
            errorTrKey !== localErrorsMessages["007"] || formData.authorizationCode !== value,
          triggerEvent: "change",
        },
      ],
      newPassword: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey,
          validate: (value) =>
            errorTrKey !== localErrorsMessages["013"] || formData.newPassword !== value,
          triggerEvent: "change",
        },
      ],
      confirmedNewPassword: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey: "passwordForm.passwordsNotMatch",
          validate: (value, allValues) => value === "" || value === allValues.newPassword,
          triggerEvent: "blur",
        },
      ],
    }),
    [errorTrKey, formData]
  );

  return (
    <FormLayout
      titleTrKey={selectedMode.titleTrKey}
      submitTrKey="button.save"
      initialState={initialState}
      validationSchema={validationSchema}
      validator={{
        titleTrKey: "passwordForm.passwordConditionsTitle",
        conditions: newPasswordValidationConditions,
        phraseFieldId: "newPassword",
      }}
      onSubmitAction={(fieldsValue) => {
        let parameters: any = { authorizationCode: fieldsValue.authorizationCode };

        if (props.mode === passwordFormModes.set) {
          parameters = {
            ...parameters,
            password: fieldsValue.newPassword,
            pid: formData.pid,
            policyNo: formData.policyNo,
          };
        } else {
          parameters = {
            ...parameters,
            newPassword: fieldsValue.newPassword,
            username: formData.username,
          };
        }

        return selectedMode.dispatchAction(parameters);
      }}
      leftTitleTrKey="registerForm.enterOneTimepassword"
      leftComponent={(props) => {
        return (
          <AuthorizationCodeInput
            {...props}
            onResendAction={selectedMode.sendAuthMessageAgainAction}
          />
        );
      }}
      rightTitleTrKey="registerForm.setNewPassword"
      rightComponent={({
        fieldsValue,
        onFieldValueChange,
        onFieldValueBlur,
        hasValidationError,
        getValidationMessagesTrKeys,
      }) => {
        const newPasswordValidationMessagesTrKeys = getValidationMessagesTrKeys("newPassword");
        return (
          <>
            <InputFormElement
              labelProps={{
                labelTrKey: "passwordForm.newPasswordPlaceholder",
              }}
              inputProps={{
                value: fieldsValue.newPassword,
                onChange: onFieldValueChange("newPassword", "confirmedNewPassword"),
                onBlur: onFieldValueBlur("newPassword"),
                isInvalid: hasValidationError("newPassword"),
                inputType: "password",
                validationMessagesTrKeys: newPasswordValidationMessagesTrKeys,
              }}
            />
            <InputFormElement
              labelProps={{
                labelTrKey: "passwordForm.repeatNewPasswordPlaceholder",
              }}
              inputProps={{
                value: fieldsValue.confirmedNewPassword,
                onChange: onFieldValueChange("confirmedNewPassword", "newPassword"),
                onBlur: onFieldValueBlur("confirmedNewPassword"),
                isInvalid: hasValidationError("confirmedNewPassword"),
                inputType: "password",
                validationMessagesTrKeys: getValidationMessagesTrKeys("confirmedNewPassword"),
              }}
            />
          </>
        );
      }}
    />
  );
};
